// API service for user-related operations

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface UserProfile {
  id: string;
  username: string;
  email: string;
  depositAddress: string;
  balance: number;
  xp: number;
  level: number;
  totalWagered: number;
  totalWon: number;
  totalLost: number;
  isAdmin: boolean;
  createdAt: string;
  lastActive: string;
  settings: {
    dailyLossLimit: number;
    sessionTimeLimit: number;
    realityCheckInterval: number;
  };
}

class UserService {
  private getAuthHeaders(): { [key: string]: string } {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  }

  async getCurrentUser(): Promise<UserProfile | null> {
    try {
      const headers = this.getAuthHeaders();
      
      // Don't make request if no token
      if (!headers.Authorization) {
        return null;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
        method: 'GET',
        headers,
      });
      
      // Handle 401 silently - user is not authenticated
      if (response.status === 401) {
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }

      // Backend returns { user: {...}, statistics: {...} }
      const userData = result.user;
      if (userData) {
        // Convert BigInt strings to numbers
        userData.balance = Number(userData.balance || 0);
        userData.totalWagered = Number(userData.totalWagered || 0);
        userData.totalWon = Number(userData.totalWon || 0);
        userData.totalLost = Number(userData.totalLost || 0);
      }
      return userData || null;
    } catch (error) {
      // Silently return null - calling code will handle appropriately
      return null;
    }
  }

  async refreshUserBalance(): Promise<number | null> {
    try {
      const user = await this.getCurrentUser();
      return user?.balance || null;
    } catch (error) {
      console.error('Error refreshing user balance:', error);
      return null;
    }
  }

  async getTransactions(page = 1, limit = 20, search = '', type = '') {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(type && { type }),
      });

      const response = await fetch(`${API_BASE_URL}/api/users/transactions?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  async addXP(amount: number): Promise<{ xp: number; level: number; leveledUp: boolean } | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/add-xp`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ amount }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Error adding XP:', error);
      return null;
    }
  }

  async getLevelInfo(): Promise<any | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/level-info`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Error fetching level info:', error);
      return null;
    }
  }
}

export const userService = new UserService();
export default UserService;